import { FC, useState, useEffect } from 'react';
import { PrivacyPolicy } from '../../pages/LegalPage';

interface CookieConsentProps {
    onAccept: () => void;
    onDecline: () => void;
}

export const CookieConsent: FC<CookieConsentProps> = ({ onAccept, onDecline }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [showPrivacyDetails, setShowPrivacyDetails] = useState(false);

    useEffect(() => {
        const consent = localStorage.getItem('cookie_consent');
        if (!consent) {
            // Show banner after a short delay for better UX
            setTimeout(() => setIsVisible(true), 1000);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem('cookie_consent', 'accepted');
        localStorage.setItem('cookie_consent_date', new Date().toISOString());
        setIsVisible(false);
        onAccept();
    };

    const handleDecline = () => {
        localStorage.setItem('cookie_consent', 'declined');
        localStorage.setItem('cookie_consent_date', new Date().toISOString());
        setIsVisible(false);
        onDecline();
    };

    if (!isVisible) return null;

    return (
        <>
            {/* Dark backdrop overlay */}
            <div className="fixed inset-0 z-[99] bg-black/70 backdrop-blur-sm" />

            <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 animate-slideUp">
                <div className="max-w-lg mx-auto bg-[#1a1a2e] rounded-2xl border border-glass-border shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
                    {/* Header with icon */}
                    <div className="bg-gradient-to-r from-primary/30 to-secondary/30 p-4 border-b border-glass-border shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/30 flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary">cookie</span>
                            </div>
                            <div>
                                <h3 className="font-bold text-white">Cookies & Confidentialité</h3>
                                <p className="text-xs text-gray-300">Nous respectons votre vie privée</p>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-4 overflow-y-auto">
                        <p className="text-sm text-gray-300 leading-relaxed">
                            Nous utilisons des <strong className="text-white">cookies techniques</strong> nécessaires
                            au bon fonctionnement de l'application (authentification, préférences).
                            Aucun cookie publicitaire n'est utilisé.
                        </p>

                        <div className="bg-[#252540] rounded-lg p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-success text-sm">check_circle</span>
                                <span className="text-xs text-gray-300">Cookies d'authentification</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-success text-sm">check_circle</span>
                                <span className="text-xs text-gray-300">Préférences (thème, notifications)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-error text-sm">cancel</span>
                                <span className="text-xs text-gray-400 line-through">Cookies publicitaires</span>
                            </div>
                        </div>

                        <p className="text-xs text-gray-400">
                            En savoir plus dans notre{' '}
                            <button
                                onClick={() => setShowPrivacyDetails(true)}
                                className="text-primary hover:underline font-bold bg-transparent border-none p-0 cursor-pointer"
                            >
                                Politique de confidentialité
                            </button>
                        </p>

                        {/* Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={handleDecline}
                                className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm bg-[#252540] text-gray-300 hover:bg-[#303050] transition-colors"
                            >
                                Refuser
                            </button>
                            <button
                                onClick={handleAccept}
                                className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm bg-primary text-white hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                            >
                                Accepter
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Nested Privacy Policy Modal */}
            {showPrivacyDetails && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 animate-fadeIn">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setShowPrivacyDetails(false)} />
                    <div className="relative w-full max-w-2xl bg-[#1a1a2e] rounded-2xl border border-glass-border shadow-2xl max-h-[85vh] flex flex-col">
                        <div className="p-4 border-b border-white/10 flex justify-between items-center bg-surface-2 rounded-t-2xl">
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary">shield</span>
                                Politique de Confidentialité
                            </h3>
                            <button
                                onClick={() => setShowPrivacyDetails(false)}
                                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto custom-scrollbar">
                            <PrivacyPolicy />
                        </div>
                        <div className="p-4 border-t border-white/10 bg-surface-2 rounded-b-2xl flex justify-end">
                            <button
                                onClick={() => setShowPrivacyDetails(false)}
                                className="px-6 py-2 bg-primary text-white rounded-lg font-bold hover:bg-primary-hover transition-colors"
                            >
                                J'ai compris
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Utility functions for checking consent
export const hasCookieConsent = (): boolean => {
    return localStorage.getItem('cookie_consent') === 'accepted';
};

export const getCookieConsentDate = (): string | null => {
    return localStorage.getItem('cookie_consent_date');
};

export const resetCookieConsent = (): void => {
    localStorage.removeItem('cookie_consent');
    localStorage.removeItem('cookie_consent_date');
};
